import { ExpectedError } from "./expectedError";
import { parseVolumeDirective } from "./volumes/directive";
import type { VolumeDirective } from "./volumes/types";

type Instruction =
  | { kind: "run"; command: string; cacheOutputs: string[] }
  | { kind: "copy"; sources: string[] }
  | { kind: "cmd"; command: string }
  | { kind: "volume"; volume: VolumeDirective };

export function usesBaseImage(content: string | null | undefined) {
  return firstInstruction(content) === "FROM 3b/base";
}

function firstInstruction(content: string | null | undefined) {
  return content
    ?.split("\n")
    .map((line) => line.trim())
    .find((line) => line && !line.startsWith("#"));
}

export function tryParseDockerfile(content: string): Instruction[] {
  try {
    return parseDockerfile(content);
  } catch {
    return [];
  }
}

// Matches BuildKit heredoc openers such as <<EOF, <<-EOF, and <<'EOF' as whole words, but not here-strings (<<<) or shifts like $((1<<n)).
const HEREDOC = /(?<=\s)<<(?!<)-?(["']?)([A-Za-z_][A-Za-z0-9_]*)\1(?=\s|$)/g;

// Image Dockerfiles may have several stages, and only the final stage describes the step, so earlier stages are dropped.
export function parseDockerfile(content: string): Instruction[] {
  let instructions: Instruction[] = [];
  const lines = content.split("\n");
  let i = 0;
  let sawFrom = false;
  let legacyBase = false;
  let leadingArg = false;

  while (i < lines.length) {
    let line = lines[i]!.trim();

    if (line === "" || line.startsWith("#")) {
      i++;
      continue;
    }

    while (line.endsWith("\\") && i + 1 < lines.length) {
      line = `${line.slice(0, -1).trimEnd()} ${lines[++i]!.trim()}`;
    }

    if (line.startsWith("FROM ")) {
      if (!sawFrom) {
        legacyBase = line === "FROM 3b/base";
        if (legacyBase && leadingArg)
          throw new ExpectedError("Dockerfile must start with FROM");
      } else if (legacyBase) {
        throw new ExpectedError("Multiple FROM instructions are not supported");
      } else if (instructions.some((inst) => inst.kind === "volume")) {
        throw new ExpectedError(
          "VOLUME is only supported in the final build stage"
        );
      } else {
        instructions = [];
      }
      sawFrom = true;
      i++;
      continue;
    }

    if (!sawFrom) {
      if (line.startsWith("ARG ")) {
        leadingArg = true;
        i++;
        continue;
      }
      throw new ExpectedError("Dockerfile must start with FROM");
    }

    if (!legacyBase && /^(RUN|COPY|ADD) /.test(line)) {
      for (const [, , delimiter] of line.matchAll(HEREDOC)) {
        i++;
        while (i < lines.length && lines[i]!.trim() !== delimiter) i++;
        if (i >= lines.length)
          throw new ExpectedError(`Heredoc <<${delimiter} has no closing line`);
      }
    }

    if (line.startsWith("RUN ")) {
      let rest = line.slice(4);
      if (!legacyBase) {
        i++;
        continue;
      }
      const mountRe = /--mount=type=cache,target=([^,\s]+)\S*\s*/g;
      const cacheOutputs = [...rest.matchAll(mountRe)].map((m) => m[1]!);
      // Targets are workdir-relative segments, not filesystem paths. Reject
      // invalid ones here so the agent sees a fixable error at parse time
      const validTarget = /^[A-Za-z0-9_.-]+$/;
      for (const target of cacheOutputs) {
        if (target === "." || target === ".." || !validTarget.test(target)) {
          throw new ExpectedError(
            `Invalid --mount=type=cache,target=${target}: target must be a single workdir-relative name (letters, digits, '.', '_', '-'), like \`node_modules\` or \`bun\`. To add npm packages, edit package.json — the existing ./bun install layer already caches node_modules.`
          );
        }
      }
      rest = rest.replace(mountRe, "").trim();

      const heredocMatch = rest.match(/^<<'(\w+)'\s*$/);
      if (heredocMatch) {
        const delimiter = heredocMatch[1]!;
        const bodyLines: string[] = [];
        i++;
        while (i < lines.length && lines[i]!.trim() !== delimiter) {
          bodyLines.push(lines[i]!);
          i++;
        }
        instructions.push({
          kind: "run",
          command: bodyLines.join("\n").trim(),
          cacheOutputs,
        });
        i++;
      } else {
        instructions.push({ kind: "run", command: rest, cacheOutputs });
        i++;
      }
      continue;
    }

    if (line.startsWith("COPY ")) {
      const parts = line.slice(5).trim().split(/\s+/);
      const sources = parts.slice(0, -1);
      instructions.push({ kind: "copy", sources });
      i++;
      continue;
    }

    if (line.startsWith("CMD ")) {
      instructions.push({ kind: "cmd", command: line.slice(4) });
      i++;
      continue;
    }

    if (line.startsWith("VOLUME ")) {
      const volume = parseVolumeDirective(line);
      if (volume) instructions.push({ kind: "volume", volume });
      i++;
      continue;
    }

    i++;
  }

  if (leadingArg && !sawFrom)
    throw new ExpectedError("Dockerfile must start with FROM");
  return instructions;
}
